import { useMemo, useState, type ReactNode } from 'react';
import { DECKS, DEFAULT_DECK, nextDeckId } from '../constants/decks';
import type { DeckId } from '../../types/deck';
import { DeckContext, type DeckContextValue } from './deck-context';

const STORAGE_KEY = 'arcana.deck';

function loadInitialDeck(): DeckId {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored && stored in DECKS) return stored as DeckId;
    } catch {
        /* localStorage unavailable (private mode etc.) — fall through to default. */
    }
    return DEFAULT_DECK;
}

function persistDeck(id: DeckId) {
    try {
        window.localStorage.setItem(STORAGE_KEY, id);
    } catch {
        /* Non-fatal: the deck still switches for this session. */
    }
}

/** Provides the active-deck state to the whole app. See `useDeck`. */
export function DeckProvider({ children }: { children: ReactNode }) {
    const [deckId, setDeckIdState] = useState<DeckId>(loadInitialDeck);

    const value = useMemo<DeckContextValue>(() => ({
        deckId,
        setDeckId: (id) => {
            setDeckIdState(id);
            persistDeck(id);
        },
        cycleDeck: () => {
            setDeckIdState((prev) => {
                const next = nextDeckId(prev);
                persistDeck(next);
                return next;
            });
        },
    }), [deckId]);

    return <DeckContext.Provider value={value}>{children}</DeckContext.Provider>;
}
